/**
 * Replays (replacing VK notes, FWP.as:14784-14817, 18364-18371) and operator news.
 *
 * Replays come from three places: `replay.save` (the client's own recording), finished sessions (SessionService) and
 * solo battles finished with player.command 'battle.finish' (BattleService.saveBattle: the server-built init with the
 * pre-battle state, the seed, the command log and the verdict). Lists are paginated (design D9): newest first, an
 * opaque cursor of the last item returned as `next`. A replay's pre-battle game state is the owner's private data:
 * `get` hands it to the owner only.
 *
 * Listing and trimming never read the replays themselves: a replay holds a command log of up to a megabyte, and
 * parsing every player's replays on each replay.list, replay.save or battle.finish blocked the server for everyone as
 * the collection grew. The service keeps an in-memory index of the replays' summaries by player instead, built from
 * the collection once (first use) and kept in step with every write this service makes (the only writer of the
 * collection); a battle's replay enters it once the batch that stores it is written (BattleReplayWrites.committed).
 *
 * One replay per battle: `replay.save` with a `battleId` is idempotent. A second save of the same battle stores
 * nothing and answers the replay stored first; when the server already keeps that battle (battle.finish), the
 * client's legacy string joins that record instead of a second one; a battle finished after the client saved it
 * takes the saved legacy string into its own record, which replaces the saved one (battleWrites). Saves of one
 * player's battle run one after another, so two at once (a double tap, a retry) cannot both store it.
 */
import { type JsonValue, cloneJson, isPlainObject } from '../../../shared/json';
import { randomId } from '../../../shared/random';
import { BackendError } from '../../../shared/protocol/errors';
import type { PayloadOf } from '../../../shared/protocol/messages';
import type { BattleInit, NewsItem, PlayerId, ReplayRecord, ReplaySummary, ValidationVerdict } from '../../../shared/protocol/model';
import { CommandLog, type CommandLogData } from '../../../session/command-log';
import { parseLegacyReplay } from '../../../session/replay-codec';
import type { SoloBattleInit } from '../battle-sim';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import type { ListPage } from '../../../shared/protocol/messages';
import { LIST_LIMITS } from '../../../shared/protocol/validators';
import { cursorInt, decodeCursor as decodeListCursor, encodeCursor as encodeListCursor, keysetPage, pageLimit } from '../pagination';
import { type ServiceDeps, C } from './deps';

/** A solo battle of player.command to store as a replay (BattleService). */
export interface BattleReplayInput {
  /** Pre-generated replay id (the battle.finish result names it before the replay is written). */
  replayId?: string;
  battleId: string;
  /** The server-built battle init (mode, stage / zone, pre-battle state, stims, annihilators, team name). */
  init: SoloBattleInit;
  seed: number;
  log: CommandLogData;
  /** Winner of the outcome the server applied. */
  winner: number;
  verdict: ValidationVerdict;
  /** settings.skill at the start (difficulty, +10 hardcore), when known. */
  skill?: number | null;
}

/** The storage writes of a battle's replay and the index update to make once they are written (ReplayService.battleWrites). */
export interface BattleReplayWrites {
  record: ReplayRecord;
  ops: StorageBatchOp[];
  /** Call after the batch holding `ops` was written: the replay enters the index, the trimmed ones leave it. */
  committed(): void;
}

/** A page of a player's replays and the cursor of the next one (null = last page). */
export interface ReplayPage {
  items: ReplaySummary[];
  next: string | null;
}

export const DEFAULT_REPLAY_PAGE = 20;

/** Newest first; replays of the same millisecond by id, descending (the order the cursor follows). */
function newestFirst(a: Pick<ReplayRecord, 'createdAt' | 'replayId'>, b: Pick<ReplayRecord, 'createdAt' | 'replayId'>): number {
  return b.createdAt - a.createdAt || (a.replayId < b.replayId ? 1 : a.replayId > b.replayId ? -1 : 0);
}

function encodeCursor(r: Pick<ReplayRecord, 'createdAt' | 'replayId'>): string {
  return `${r.createdAt}.${r.replayId}`;
}

function decodeCursor(cursor: string): { createdAt: number; replayId: string } {
  const m = /^(\d{1,16})\.([A-Za-z0-9_-]{1,64})$/.exec(cursor);
  const createdAt = m ? Number(m[1]) : NaN;
  if (!m || !Number.isSafeInteger(createdAt)) throw new BackendError('BAD_REQUEST', 'Unknown replay list cursor', { reason: 'bad_cursor' });
  return { createdAt, replayId: m[2] };
}

/** A replay without the owner's private game state (see ReplayService.get). */
function publicReplay(r: ReplayRecord): ReplayRecord {
  const params = r.init?.params;
  if (!r.init || !isPlainObject(params) || !('gameState' in params)) return r;
  const { gameState: _private, ...rest } = params;
  return { ...r, init: { ...r.init, params: rest } };
}

function summaryOf(r: ReplayRecord): ReplaySummary {
  const s: ReplaySummary = { replayId: r.replayId, playerId: r.playerId, createdAt: r.createdAt, mode: r.mode, stage: r.stage, result: r.result, verified: r.verified, sessionId: r.sessionId };
  if (r.verdict !== undefined) s.verdict = r.verdict;
  if (r.battleId !== undefined) s.battleId = r.battleId;
  return s;
}

/** Summaries of the stored replays: player -> replay id -> summary. */
type ReplayIndex = Map<PlayerId, Map<string, ReplaySummary>>;

export class ReplayService {
  private index: ReplayIndex | null = null;
  private loading: Promise<ReplayIndex> | null = null;
  /** The running save of a player's battle (`playerId battleId`): the next save of it waits for it. */
  private readonly saving = new Map<string, Promise<unknown>>();

  constructor(private readonly d: ServiceDeps) {}

  /** The index of summaries (see the module comment); the first call reads the collection once. */
  private async summaries(): Promise<ReplayIndex> {
    if (this.index) return this.index;
    this.loading ??= (async () => {
      const index: ReplayIndex = new Map();
      for (const r of await this.d.storage.list<ReplayRecord>(C.replays)) {
        if (r && typeof r.replayId === 'string' && typeof r.playerId === 'string') indexAdd(index, summaryOf(r));
      }
      this.index = index;
      return index;
    })();
    try {
      return await this.loading;
    } catch (err) {
      this.loading = null;
      throw err;
    }
  }

  /** The player's summaries, newest first. */
  private async mine(playerId: PlayerId): Promise<ReplaySummary[]> {
    return [...((await this.summaries()).get(playerId)?.values() ?? [])].sort(newestFirst);
  }

  /** The stored replay of the player's battle `battleId` (summary), if any. */
  private async ofBattle(playerId: PlayerId, battleId: string): Promise<ReplaySummary | null> {
    for (const r of (await this.summaries()).get(playerId)?.values() ?? []) if (r.battleId === battleId) return r;
    return null;
  }

  /** Runs `run` after every earlier call of the same key has settled. */
  private serial<T>(key: string, run: () => Promise<T>): Promise<T> {
    const next = (this.saving.get(key) ?? Promise.resolve()).catch(() => undefined).then(run);
    this.saving.set(key, next);
    const done = () => {
      if (this.saving.get(key) === next) this.saving.delete(key);
    };
    next.then(done, done);
    return next;
  }

  async save(playerId: PlayerId, p: PayloadOf<'replay.save'>, extra: { sessionId?: string; verified?: boolean; verdict?: ValidationVerdict } = {}): Promise<ReplayRecord> {
    if (p.battleId === undefined) return this.store(playerId, p, extra);
    const battleId = p.battleId;
    return this.serial(`${playerId} ${battleId}`, async () => {
      const known = await this.ofBattle(playerId, battleId);
      if (!known) return this.store(playerId, p, extra);
      const r = await this.d.storage.get<ReplayRecord>(C.replays, known.replayId);
      if (!r) return this.store(playerId, p, extra);
      // The battle the server keeps (battle.finish) gets the client's legacy string once; anything else stays.
      if (r.legacy === null && p.legacy !== undefined) {
        this.checkLegacy(p.legacy);
        const merged: ReplayRecord = { ...r, legacy: p.legacy };
        await this.d.storage.put(C.replays, merged.replayId, merged);
        return merged;
      }
      return r;
    });
  }

  private checkLegacy(legacy: string): void {
    let ok = false;
    try {
      ok = parseLegacyReplay(legacy).checksumOk;
    } catch {
      ok = false;
    }
    if (!ok) throw new BackendError('VALIDATION_FAILED', 'Legacy replay string is malformed or its checksum does not match', { reason: 'bad_legacy_replay' });
  }

  private async store(playerId: PlayerId, p: PayloadOf<'replay.save'>, extra: { sessionId?: string; verified?: boolean; verdict?: ValidationVerdict }): Promise<ReplayRecord> {
    if (p.legacy !== undefined) this.checkLegacy(p.legacy);
    const rec: ReplayRecord = {
      replayId: randomId(this.d.rng, 'r_', 10),
      playerId,
      createdAt: this.d.clock.now(),
      mode: p.mode,
      stage: p.stage,
      result: p.result,
      sessionId: extra.sessionId ?? null,
      seed: p.seed ?? null,
      init: p.init ?? null,
      log: p.log ?? null,
      legacy: p.legacy ?? null,
      verified: extra.verified ?? false,
      logHash: p.log ? CommandLog.fromJSON(p.log).hash() : null,
    };
    if (extra.verdict !== undefined) rec.verdict = extra.verdict;
    if (p.battleId !== undefined) rec.battleId = p.battleId;
    await this.d.storage.put(C.replays, rec.replayId, rec);
    indexAdd(await this.summaries(), summaryOf(rec));
    await this.trim(playerId);
    return rec;
  }

  /**
   * Stores a solo battle finished with player.command 'battle.finish'. The protocol init is the battle's mode and
   * stage (zone + zoneStage of an assault) with the whole server-built SoloBattleInit in `params`, so the replay can
   * be re-simulated exactly as the server verified it.
   */
  async saveBattle(playerId: PlayerId, b: BattleReplayInput): Promise<ReplayRecord> {
    const writes = await this.battleWrites(playerId, b);
    await this.d.storage.batch(writes.ops);
    writes.committed();
    return writes.record;
  }

  /**
   * The replay record of a solo battle and its storage writes (the record, and the deletes of the player's oldest
   * replays beyond config.replays.maxPerPlayer), for the battle command, which stores them in one batch with the
   * player's profile (saveBattle stores them alone). The caller calls `committed()` once that batch is written.
   */
  async battleWrites(playerId: PlayerId, b: BattleReplayInput): Promise<BattleReplayWrites> {
    const init: BattleInit = { mode: b.init.mode, stage: b.init.stage, params: b.init as unknown as JsonValue };
    if (b.init.mode === 'assault') {
      init.zone = b.init.stage;
      init.zoneStage = b.init.zoneStage ?? 0;
    }
    if (typeof b.skill === 'number') init.skill = b.skill;
    const rec: ReplayRecord = {
      replayId: b.replayId ?? randomId(this.d.rng, 'r_', 10),
      playerId,
      createdAt: this.d.clock.now(),
      mode: b.init.mode,
      stage: b.init.stage,
      result: { winnerTeam: b.winner },
      sessionId: null,
      seed: b.seed,
      init,
      log: b.log,
      legacy: null,
      verified: b.verdict === 'verified',
      logHash: CommandLog.fromJSON(b.log).hash(),
      verdict: b.verdict,
      battleId: b.battleId,
    };
    // The client saved this battle before it was finished (replay.save { battleId }): its legacy string comes into
    // this record, which replaces that one (one replay per battle).
    const saved = await this.ofBattle(playerId, b.battleId);
    const replaced = saved && saved.replayId !== rec.replayId ? saved.replayId : null;
    if (replaced) {
      const old = await this.d.storage.get<ReplayRecord>(C.replays, replaced);
      if (old && old.legacy) rec.legacy = old.legacy;
    }
    const trimmed = await this.trimmedBy(playerId, rec, replaced);
    const gone = replaced ? [replaced, ...trimmed] : trimmed;
    const index = await this.summaries();
    return {
      record: rec,
      ops: [putOp(C.replays, rec.replayId, rec), ...gone.map((id) => deleteOp(C.replays, id))],
      committed: () => {
        indexAdd(index, summaryOf(rec));
        for (const id of gone) indexRemove(index, playerId, id);
      },
    };
  }

  private async trim(playerId: PlayerId): Promise<void> {
    const mine = await this.mine(playerId);
    for (const old of mine.slice(this.d.config.replays.maxPerPlayer)) {
      await this.d.storage.delete(C.replays, old.replayId);
      indexRemove(await this.summaries(), playerId, old.replayId);
    }
  }

  /** The ids of the player's oldest replays beyond the limit once `added` is stored (and `replaced` deleted). */
  private async trimmedBy(playerId: PlayerId, added: ReplayRecord, replaced: string | null = null): Promise<string[]> {
    const mine = [summaryOf(added), ...(await this.mine(playerId)).filter((r) => r.replayId !== added.replayId && r.replayId !== replaced)];
    mine.sort(newestFirst);
    return mine.slice(this.d.config.replays.maxPerPlayer).map((old) => old.replayId);
  }

  /** An operator restored a damaged replay (admin server.repairDocument): the index, built without it, takes it in. */
  async repaired(record: ReplayRecord): Promise<void> {
    if (!record || typeof record.replayId !== 'string' || typeof record.playerId !== 'string') return;
    indexAdd(await this.summaries(), summaryOf(record));
  }

  /** Account deletion: the ids of every replay the player owns (from the index; the replays are not read). */
  async idsOf(playerId: PlayerId): Promise<string[]> {
    return [...((await this.summaries()).get(playerId)?.keys() ?? [])];
  }

  /** Account deletion: after the batch that deleted them, the player's replays `ids` leave the index. */
  async forget(playerId: PlayerId, ids: readonly string[]): Promise<void> {
    const index = await this.summaries();
    for (const id of ids) indexRemove(index, playerId, id);
  }

  /** Operator API (admin console): the summaries of every stored replay, or of one player's (from the index). */
  async summariesOf(playerId: PlayerId | null): Promise<ReplaySummary[]> {
    const index = await this.summaries();
    if (playerId !== null) return [...(index.get(playerId)?.values() ?? [])];
    return [...index.values()].flatMap((mine) => [...mine.values()]);
  }

  /** The newest `limit` replays of a player (the first page of `page`). */
  async list(playerId: PlayerId, limit = DEFAULT_REPLAY_PAGE): Promise<ReplaySummary[]> {
    return (await this.page(playerId, { limit })).items;
  }

  /**
   * One page of a player's replays, newest first: `limit` items after `cursor` (the `next` of the previous page;
   * none = the first page). The cursor is opaque to clients; a malformed one is BAD_REQUEST 'bad_cursor'. A replay
   * deleted in between does not break the paging (the cursor is a position, not an id lookup).
   */
  async page(playerId: PlayerId, opts: { limit?: number; cursor?: string } = {}): Promise<ReplayPage> {
    const limit = Math.max(1, Math.min(100, opts.limit ?? DEFAULT_REPLAY_PAGE));
    const after = opts.cursor === undefined ? null : decodeCursor(opts.cursor);
    const mine = (await this.mine(playerId)).filter((r) => !after || r.createdAt < after.createdAt || (r.createdAt === after.createdAt && r.replayId < after.replayId));
    const items = mine.slice(0, limit);
    return { items: items.map((r) => cloneJson(r)), next: mine.length > limit ? encodeCursor(items[items.length - 1]) : null };
  }

  /**
   * A replay as `viewer` may see it: the owner gets the whole record; anybody else (or no viewer) gets it without the
   * player's private game state (init.params.gameState: the pre-battle state of a solo battle, with gold, diamonds,
   * inventory, settings and tasks), which only the owner and the server may read.
   */
  async get(replayId: string, viewer: PlayerId | null = null): Promise<ReplayRecord> {
    const r = await this.d.storage.get<ReplayRecord>(C.replays, replayId);
    if (!r) throw new BackendError('NOT_FOUND', 'Replay not found', { reason: 'replay_not_found', replayId });
    return viewer !== null && viewer === r.playerId ? r : publicReplay(r);
  }
}

function indexAdd(index: ReplayIndex, summary: ReplaySummary): void {
  let mine = index.get(summary.playerId);
  if (!mine) index.set(summary.playerId, (mine = new Map()));
  mine.set(summary.replayId, summary);
}

function indexRemove(index: ReplayIndex, playerId: PlayerId, replayId: string): void {
  const mine = index.get(playerId);
  if (!mine) return;
  mine.delete(replayId);
  if (mine.size === 0) index.delete(playerId);
}

export class NewsService {
  constructor(private readonly d: ServiceDeps) {}

  /** Operator API (not exposed through the protocol). */
  async publish(title: string, body: string): Promise<NewsItem> {
    const item: NewsItem = { newsId: randomId(this.d.rng, 'n_', 8), title, body, publishedAt: this.d.clock.now() };
    await this.d.storage.put(C.news, item.newsId, item);
    return item;
  }

  /** The newest `limit` news, newest first (publishedAt, then newsId, both descending: the order `page` follows). */
  async list(limit = 20): Promise<NewsItem[]> {
    return (await this.d.storage.list<NewsItem>(C.news))
      .sort((a, b) => b.publishedAt - a.publishedAt || (a.newsId < b.newsId ? 1 : a.newsId > b.newsId ? -1 : 0))
      .slice(0, limit);
  }

  /**
   * One page of the news, newest first (publishedAt, then newsId, both descending): `limit` items after `cursor` (the
   * `next` of the previous page). Cursor `nw.<publishedAt>.<newsId>`; a malformed one is BAD_REQUEST 'bad_cursor'.
   */
  async page(opts: { limit?: number; cursor?: string } = {}): Promise<ListPage<NewsItem>> {
    const limit = pageLimit(opts.limit, LIST_LIMITS['news.list'].default, LIST_LIMITS['news.list'].max);
    let after: ((n: NewsItem) => boolean) | null = null;
    if (opts.cursor !== undefined) {
      const [at, id] = decodeListCursor(opts.cursor, 'nw', 2);
      const publishedAt = cursorInt(at);
      after = (n) => n.publishedAt < publishedAt || (n.publishedAt === publishedAt && n.newsId < id);
    }
    return keysetPage(await this.list(Number.MAX_SAFE_INTEGER), limit, after, (n) => encodeListCursor('nw', [n.publishedAt, n.newsId]));
  }
}
